import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wallet, RefreshCw, Search, Check, X, Send, ShieldAlert } from 'lucide-react';
import { fetchWalletRequests, approveDeposit, rejectWalletRequest, completeWithdrawal } from './hooks/hooks';

const NAV = '#001f3f';
const TEAL = '#0d9488';

const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
         style={{ backgroundColor: accent + '18' }}>
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Badge = ({ children, color = 'slate' }) => {
  const map = {
    teal:   'bg-teal-100 text-teal-700 border-teal-200',
    amber:  'bg-amber-100 text-amber-700 border-amber-200',
    red:    'bg-red-100 text-red-700 border-red-200',
    blue:   'bg-blue-100 text-blue-700 border-blue-200',
    slate:  'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[color]}`}>
      {children}
    </span>
  );
};

const statusColor = (status) => ({
  pending_review: 'amber',
  pending: 'amber',
  completed: 'teal',
  rejected: 'red',
  failed: 'red',
}[status] || 'slate');

const fmtMoney = (n) => `TZS ${Number(n ?? 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function WalletRequests() {
  const [type, setType] = useState('deposit');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [feeDraft, setFeeDraft] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const queryClient = useQueryClient();

  // Deposits default to the 'pending_review' status; withdrawals use 'pending'.
  const effectiveStatus = statusFilter === 'pending'
    ? (type === 'deposit' ? 'pending_review' : 'pending')
    : statusFilter;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['wallet-requests', type, effectiveStatus],
    queryFn: () => fetchWalletRequests({ type, status: effectiveStatus }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['wallet-requests'] });

  const approveMutation = useMutation({
    mutationFn: ({ id, fees }) => approveDeposit(id, fees),
    onSuccess: invalidate,
  });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => rejectWalletRequest(id, reason),
    onSuccess: () => { invalidate(); setRejectingId(null); setRejectReason(''); },
  });
  const completeMutation = useMutation({
    mutationFn: (id) => completeWithdrawal(id),
    onSuccess: invalidate,
  });

  const records = (data?.data || []).filter(r =>
    !search ||
    r.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    r.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.provider_reference?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPending = records.reduce((sum, r) => sum + (r.status === 'pending_review' || r.status === 'pending' ? r.amount : 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Wallet Requests</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Deposits never auto-credit — verify the payment reference against your provider/bank statement before approving.
          </p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Safety banner */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          No payment provider is connected yet, so every deposit request must be manually verified here before it's credited.
          Check the provider + reference code against your mobile money/bank statement first.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Wallet} label={`Pending ${type === 'deposit' ? 'Deposits' : 'Withdrawals'}`} value={records.length} accent={NAV} />
        <StatCard icon={Wallet} label="Pending Amount" value={fmtMoney(totalPending)} accent={TEAL} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {['deposit', 'withdraw'].map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-4 py-2 text-xs font-semibold ${type === t ? 'text-white' : 'text-slate-600 bg-white hover:bg-slate-50'}`}
              style={type === t ? { backgroundColor: NAV } : {}}
            >
              {t === 'deposit' ? 'Deposits' : 'Withdrawals'}
            </button>
          ))}
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="pending">Pending review</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, or reference…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Provider</th>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!isLoading && records.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No requests found.</td></tr>
            )}
            {records.map(r => {
              const fees = feeDraft[r.id] || { provider_fee: '0', platform_fee: '0' };
              const isPendingDeposit = r.transaction_type === 'deposit' && r.status === 'pending_review';
              const isPendingWithdraw = r.transaction_type === 'withdraw' && r.status === 'pending';
              return (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{r.user_name || r.user_email}</p>
                    <p className="text-xs text-slate-400">{r.user_email}</p>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{fmtMoney(r.amount)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.provider || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.provider_reference || '—'}</td>
                  <td className="px-4 py-3"><Badge color={statusColor(r.status)}>{r.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(r.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {isPendingDeposit && (
                        <>
                          <input
                            type="number" placeholder="Fee"
                            value={fees.provider_fee}
                            onChange={e => setFeeDraft(prev => ({ ...prev, [r.id]: { ...fees, provider_fee: e.target.value } }))}
                            className="w-20 px-2 py-1 text-xs border border-slate-200 rounded"
                            title="Provider fee to deduct"
                          />
                          <button
                            onClick={() => approveMutation.mutate({ id: r.id, fees: { provider_fee: fees.provider_fee, platform_fee: fees.platform_fee } })}
                            disabled={approveMutation.isPending}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                            style={{ backgroundColor: TEAL }}
                          >
                            <Check size={13} /> Approve
                          </button>
                        </>
                      )}
                      {isPendingWithdraw && (
                        <button
                          onClick={() => completeMutation.mutate(r.id)}
                          disabled={completeMutation.isPending}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                          style={{ backgroundColor: TEAL }}
                        >
                          <Send size={13} /> Mark Paid Out
                        </button>
                      )}
                      {(isPendingDeposit || isPendingWithdraw) && (
                        rejectingId === r.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              autoFocus
                              value={rejectReason}
                              onChange={e => setRejectReason(e.target.value)}
                              placeholder="Reason…"
                              className="w-32 px-2 py-1 text-xs border border-slate-200 rounded"
                            />
                            <button
                              onClick={() => rejectMutation.mutate({ id: r.id, reason: rejectReason })}
                              className="px-2 py-1.5 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg"
                            >
                              Confirm
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRejectingId(r.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                          >
                            <X size={13} /> Reject
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
